import {useCallback, useMemo} from 'react';
import {useLazyQuery} from '@sail/data';
import {useMemoizedValue} from '@sail/react';

import type {GraphQlDocument} from '@sail/data';

import {useV1Context} from '../../shared/v1Context';
import mergeContextVariables from '../../shared/utils/mergeContextVariables';

import type {V1LazyQueryOptions, V1LazyQueryReturn} from '../types';
import type {V1ContextInput} from '../../../schema/types';

export default function useV1LazyQuery<
  TData,
  TVariables extends {v1Context: V1ContextInput},
  TPermissions,
  THookVariables extends Partial<TVariables> = {},
>(
  query: GraphQlDocument<TData, TVariables, TPermissions>,
  options: V1LazyQueryOptions<
    GraphQlDocument<TData, TVariables, TPermissions>,
    THookVariables
  >,
): V1LazyQueryReturn<
  GraphQlDocument<TData, TVariables, TPermissions>,
  THookVariables
> {
  const v1Context = useV1Context();

  const v1ContextFromHook = useMemoizedValue(
    mergeContextVariables(v1Context, options?.variables?.v1Context),
  );

  const [callback, result] = useLazyQuery<
    TData,
    TVariables,
    TPermissions,
    THookVariables
  >(query, options);

  const callbackWrapped: V1LazyQueryReturn<
    GraphQlDocument<TData, TVariables, TPermissions>
  >[0] = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (...restArgs: any[]) => {
      const cbOptions = restArgs[0];

      return callback(
        Object.assign({}, cbOptions, {
          variables: Object.assign({}, cbOptions?.variables, {
            v1Context: mergeContextVariables(
              v1ContextFromHook,
              cbOptions?.variables?.v1Context,
            ),
          }),
        }),
      );
    },
    [callback, v1ContextFromHook],
  );

  const {refetch} = result;

  const refetchWrapped = useCallback(
    (variables: Parameters<typeof refetch>[0]): ReturnType<typeof refetch> => {
      const finalVariables = Object.assign({}, variables, {
        v1Context: mergeContextVariables(
          v1ContextFromHook,
          variables?.v1Context,
        ),
      });

      return refetch(finalVariables);
    },
    [refetch, v1ContextFromHook],
  );

  const mergedResult = useMemo(() => {
    const {refetch, ...rest} = result;

    return {...rest, refetch: refetchWrapped};
  }, [result, refetchWrapped]);

  return useMemo(() => {
    return [callbackWrapped, mergedResult] as V1LazyQueryReturn<
      GraphQlDocument<TData, TVariables, TPermissions>,
      THookVariables
    >;
  }, [callbackWrapped, mergedResult]);
}
